import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, Pencil, Plus, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DataTable,
  FormInput,
  FormSelect,
  PageHeader,
  useDataTable,
  type DataTableColumn,
} from "@/components/shared";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/services/httpClient";
import {
  channelPartiesService,
  channelPartyStatusNumber,
  type ChannelLevel,
  type ChannelParty,
  type ChannelPartyStatusText,
} from "@/services/channelPartiesService";

const statusBadge: Record<string, string> = {
  Active: "bg-success/10 text-success border-success/20",
  Pending: "bg-warning/10 text-warning border-warning/20",
  Suspended: "bg-destructive/10 text-destructive border-destructive/20",
};

const statusOptions = (Object.keys(channelPartyStatusNumber) as ChannelPartyStatusText[]).map((s) => ({
  label: s,
  value: s,
}));

const copy: Record<ChannelLevel, { title: string; singular: string; description: string }> = {
  Distributor: {
    title: "Distributors",
    singular: "Distributor",
    description: "Distributors order stock directly from admin and supply wholesalers.",
  },
  Wholesaler: {
    title: "Wholesalers",
    singular: "Wholesaler",
    description: "Wholesalers order stock from their assigned distributor and supply retailers.",
  },
  Retailer: {
    title: "Retailers",
    singular: "Retailer",
    description: "Existing stores are the retailers. Link them here so they can order from a wholesaler.",
  },
};

const SEARCH_KEYS: (keyof ChannelParty)[] = ["businessName", "contactName", "phone", "email", "city", "parentBusinessName"];

const emptyForm = {
  businessName: "",
  contactName: "",
  phone: "",
  email: "",
  password: "",
  city: "",
  parentPartyId: "",
  status: "Active" as ChannelPartyStatusText,
};

type SheetState = { mode: "view" | "create" | "edit"; party: ChannelParty | null } | null;

const formatDate = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" }) : "—";

const DetailRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="grid grid-cols-3 gap-2 border-b py-3 text-sm last:border-0">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className="col-span-2 break-words font-medium">{children}</span>
  </div>
);

const AdminChannel = ({ level }: { level: ChannelLevel }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [sheet, setSheet] = useState<SheetState>(null);
  const [form, setForm] = useState(emptyForm);
  const [deleting, setDeleting] = useState<ChannelParty | null>(null);

  const { data: parties = [], isLoading } = useQuery({
    queryKey: ["admin", "channel-parties"],
    queryFn: () => channelPartiesService.getAll(),
  });

  const listed = useMemo(() => parties.filter((p) => p.levelText === level), [parties, level]);
  const distributors = useMemo(() => parties.filter((p) => p.levelText === "Distributor"), [parties]);
  const canCreate = level !== "Retailer";
  const canDelete = level !== "Retailer";

  const table = useDataTable<ChannelParty>({
    data: listed,
    searchKeys: SEARCH_KEYS,
    filterConfigs: [{ id: "statusText", options: [] }],
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["admin", "channel-parties"] });
  const onError = (title: string) => (error: unknown) =>
    toast({ title, description: getApiErrorMessage(error), variant: "destructive" });

  const createMutation = useMutation({
    mutationFn: () =>
      channelPartiesService.create({
        level,
        businessName: form.businessName.trim(),
        contactName: form.contactName.trim(),
        phone: form.phone.replace(/\D/g, "").slice(0, 10),
        email: form.email.trim(),
        password: form.password,
        city: form.city.trim() || undefined,
        parentPartyId: level === "Wholesaler" && form.parentPartyId ? Number(form.parentPartyId) : undefined,
      }),
    onSuccess: () => {
      toast({ title: `${copy[level].singular} created`, description: "Share the email and password so they can sign in." });
      refresh();
      setSheet(null);
    },
    onError: onError("Could not create account"),
  });

  const updateMutation = useMutation({
    mutationFn: (id: number) =>
      channelPartiesService.update(id, {
        businessName: form.businessName.trim(),
        contactName: form.contactName.trim(),
        phone: form.phone.replace(/\D/g, "").slice(0, 10),
        city: form.city.trim() || undefined,
        status: channelPartyStatusNumber[form.status],
      }),
    onSuccess: (updated) => {
      toast({ title: `${copy[level].singular} updated` });
      refresh();
      setSheet({ mode: "view", party: updated });
    },
    onError: onError("Could not update"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => channelPartiesService.remove(id),
    onSuccess: () => {
      toast({ title: `${copy[level].singular} deleted` });
      refresh();
      setDeleting(null);
      setSheet(null);
    },
    onError: (error) => {
      setDeleting(null);
      onError("Could not delete")(error);
    },
  });

  const syncMutation = useMutation({
    mutationFn: () => channelPartiesService.syncRetailers(),
    onSuccess: (count) => {
      toast({ title: count ? `${count} store(s) linked as retailers` : "Stores are already linked" });
      refresh();
    },
    onError: onError("Could not link stores"),
  });

  const openCreate = () => {
    setForm(emptyForm);
    setSheet({ mode: "create", party: null });
  };

  const openEdit = (party: ChannelParty) => {
    setForm({
      ...emptyForm,
      businessName: party.businessName,
      contactName: party.contactName,
      phone: party.phone,
      email: party.email ?? "",
      city: party.city ?? "",
      status: (party.statusText as ChannelPartyStatusText) || "Active",
    });
    setSheet({ mode: "edit", party });
  };

  const phoneDigits = form.phone.replace(/\D/g, "");
  const baseValid = form.businessName.trim() !== "" && form.contactName.trim() !== "" && phoneDigits.length === 10;
  const createValid =
    baseValid &&
    form.email.trim() !== "" &&
    form.password.trim().length >= 6 &&
    (level !== "Wholesaler" || form.parentPartyId !== "");

  const columns: DataTableColumn<ChannelParty>[] = [
    {
      id: "businessName",
      header: copy[level].singular,
      sortable: true,
      mobilePriority: 1,
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate font-semibold">{row.businessName}</p>
          <p className="truncate text-xs text-muted-foreground">{row.contactName}</p>
        </div>
      ),
    },
    {
      id: "phone",
      header: "Contact",
      mobileLabel: "Contact",
      cell: (row) => (
        <div className="text-xs">
          <p>{row.phone}</p>
          {row.email && <p className="text-muted-foreground">{row.email}</p>}
        </div>
      ),
    },
    { id: "city", header: "City", sortable: true, hideOnMobile: true, cell: (row) => row.city || "—" },
    ...(level === "Wholesaler"
      ? [
          {
            id: "parentBusinessName",
            header: "Distributor",
            sortable: true,
            cell: (row: ChannelParty) => row.parentBusinessName || "—",
          } satisfies DataTableColumn<ChannelParty>,
        ]
      : []),
    {
      id: "statusText",
      header: "Status",
      sortable: true,
      cell: (row) => (
        <Badge variant="outline" className={statusBadge[row.statusText] ?? ""}>
          {row.statusText}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: "",
      align: "right",
      hideOnMobile: true,
      cell: (row) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" title="View details" onClick={() => setSheet({ mode: "view", party: row })}>
            <Eye className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" title="Edit" onClick={() => openEdit(row)}>
            <Pencil className="h-4 w-4" />
          </Button>
          {canDelete && (
            <Button variant="ghost" size="icon" title="Delete" onClick={() => setDeleting(row)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  const mobileCard = (row: ChannelParty) => (
    <div className="space-y-3 rounded-lg border border-border p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-semibold">{row.businessName}</p>
          <p className="truncate text-xs text-muted-foreground">{row.contactName} • {row.phone}</p>
        </div>
        <Badge variant="outline" className={statusBadge[row.statusText] ?? ""}>
          {row.statusText}
        </Badge>
      </div>
      {(row.city || row.parentBusinessName) && (
        <p className="text-xs text-muted-foreground">
          {[row.city, row.parentBusinessName && `Under ${row.parentBusinessName}`].filter(Boolean).join(" • ")}
        </p>
      )}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" className="flex-1" onClick={() => setSheet({ mode: "view", party: row })}>
          <Eye className="mr-1.5 h-3.5 w-3.5" /> View
        </Button>
        <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(row)}>
          <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
        </Button>
        {canDelete && (
          <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => setDeleting(row)}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        )}
      </div>
    </div>
  );

  const party = sheet?.party ?? null;
  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6">
      <PageHeader
        title={copy[level].title}
        description={copy[level].description}
        actions={
          canCreate ? (
            <Button onClick={openCreate} className="gap-2">
              <Plus className="h-4 w-4" /> Add {copy[level].singular}
            </Button>
          ) : (
            <Button variant="outline" onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending}>
              {syncMutation.isPending ? "Linking…" : "Link existing stores"}
            </Button>
          )
        }
      />

      {isLoading ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : (
        <DataTable
          title={`All ${copy[level].title} (${listed.length})`}
          columns={columns}
          data={table.rows}
          getRowId={(row) => String(row.id)}
          search={table.state.search}
          onSearchChange={table.setSearch}
          searchPlaceholder={`Search ${copy[level].title.toLowerCase()}…`}
          filters={[
            {
              id: "statusText",
              placeholder: "Status",
              options: [{ label: "All status", value: "all" }, ...statusOptions],
            },
          ]}
          filterValues={table.state.filters}
          onFilterChange={table.setFilter}
          sort={table.state.sort}
          onSortChange={table.toggleSort}
          pagination={table.pagination}
          onPageChange={table.setPage}
          onPageSizeChange={table.setPageSize}
          renderMobileCard={mobileCard}
          emptyMessage={
            level === "Retailer"
              ? "No retailers yet. Use “Link existing stores”."
              : `No ${copy[level].title.toLowerCase()} yet. Use “Add ${copy[level].singular}” to register the first one.`
          }
        />
      )}

      <Sheet open={sheet !== null} onOpenChange={(open) => !open && setSheet(null)}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-xl">
          <SheetHeader className="border-b px-6 py-5 text-left">
            <SheetTitle>
              {sheet?.mode === "create"
                ? `New ${copy[level].singular}`
                : sheet?.mode === "edit"
                  ? `Edit ${party?.businessName ?? copy[level].singular}`
                  : party?.businessName}
            </SheetTitle>
            <SheetDescription>
              {sheet?.mode === "create"
                ? `Create a ${copy[level].singular.toLowerCase()} login. They sign in with the email and password below.`
                : sheet?.mode === "edit"
                  ? "Update business and contact details, or suspend the account."
                  : `${copy[level].singular} details`}
            </SheetDescription>
          </SheetHeader>

          {sheet?.mode === "view" && party && (
            <>
              <div className="flex-1 overflow-y-auto px-6 py-2">
                <DetailRow label="Level">{party.levelText}</DetailRow>
                <DetailRow label="Status">
                  <Badge variant="outline" className={statusBadge[party.statusText] ?? ""}>
                    {party.statusText}
                  </Badge>
                </DetailRow>
                <DetailRow label="Contact person">{party.contactName}</DetailRow>
                <DetailRow label="Mobile">{party.phone}</DetailRow>
                <DetailRow label="Email / login">{party.email || "—"}</DetailRow>
                <DetailRow label="City">{party.city || "—"}</DetailRow>
                {party.parentBusinessName && <DetailRow label="Distributor">{party.parentBusinessName}</DetailRow>}
                <DetailRow label="Registered on">{formatDate(party.createdAt)}</DetailRow>
              </div>
              <SheetFooter className="mt-auto border-t bg-background px-6 py-4 sm:flex-row sm:justify-end sm:gap-2">
                {canDelete && (
                  <Button
                    variant="outline"
                    className="text-destructive hover:text-destructive sm:mr-auto"
                    onClick={() => setDeleting(party)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Delete
                  </Button>
                )}
                <Button variant="outline" onClick={() => setSheet(null)}>Close</Button>
                <Button onClick={() => openEdit(party)}>
                  <Pencil className="mr-2 h-4 w-4" /> Edit
                </Button>
              </SheetFooter>
            </>
          )}

          {(sheet?.mode === "create" || sheet?.mode === "edit") && (
            <>
              <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
                {sheet.mode === "create" && level === "Wholesaler" && (
                  <FormSelect
                    label="Distributor"
                    requiredMark
                    value={form.parentPartyId}
                    onValueChange={(v) => setForm({ ...form, parentPartyId: v })}
                    placeholder="Select the distributor they buy from"
                    options={distributors.map((d) => ({ label: d.businessName, value: String(d.id) }))}
                    hint={distributors.length === 0 ? "No distributors yet. Create a distributor first." : undefined}
                  />
                )}
                <FormInput
                  label="Business name"
                  requiredMark
                  placeholder="e.g. Acme Distribution"
                  value={form.businessName}
                  onChange={(e) => setForm({ ...form, businessName: e.target.value })}
                />
                <FormInput
                  label="Contact name"
                  requiredMark
                  placeholder="Full name of contact person"
                  value={form.contactName}
                  onChange={(e) => setForm({ ...form, contactName: e.target.value })}
                />
                <FormInput
                  label="Mobile"
                  requiredMark
                  placeholder="10-digit mobile number"
                  inputMode="numeric"
                  maxLength={10}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                />
                <FormInput
                  label="Email (this is the login)"
                  requiredMark={sheet.mode === "create"}
                  type="email"
                  placeholder="name@example.com"
                  value={form.email}
                  disabled={sheet.mode === "edit"}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
                {sheet.mode === "create" && (
                  <FormInput
                    label="Password"
                    requiredMark
                    placeholder="Min 6 characters"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                  />
                )}
                <FormInput
                  label="City"
                  placeholder="e.g. Patna"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                />
                {sheet.mode === "edit" && (
                  <FormSelect
                    label="Status"
                    value={form.status}
                    onValueChange={(v) => setForm({ ...form, status: v as ChannelPartyStatusText })}
                    options={statusOptions}
                    hint="Suspended accounts cannot sign in."
                  />
                )}
              </div>
              <SheetFooter className="mt-auto border-t bg-background px-6 py-4 sm:flex-row sm:justify-end sm:gap-2">
                <Button
                  variant="outline"
                  onClick={() => (sheet.mode === "edit" && party ? setSheet({ mode: "view", party }) : setSheet(null))}
                >
                  Cancel
                </Button>
                <Button
                  disabled={saving || (sheet.mode === "create" ? !createValid : !baseValid)}
                  onClick={() => (sheet.mode === "create" ? createMutation.mutate() : party && updateMutation.mutate(party.id))}
                >
                  {saving ? "Saving…" : sheet.mode === "create" ? `Create ${copy[level].singular.toLowerCase()}` : "Save changes"}
                </Button>
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleting?.businessName}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the {copy[level].singular.toLowerCase()} and disables their login. It can't be done while they
              have wholesalers or retailers under them or any channel orders. Suspend the account instead if you only
              want to block access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleting && deleteMutation.mutate(deleting.id)}
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminChannel;
